/**
 * Service Creator - utilise les cookies SSO pour l'authentification
 */

import axios from './axiosConfig';
import NotificationService from './NotificationService';
import { normalizePhotoFromApi } from '../utils/mediaUtils';
import { normalizeSocialLinks, socialLinksForApi } from '../utils/socialLinks';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://api.local.dev:8080'}/api/shop/creator`;

const mapInfoFromApi = (data = {}) => ({
  businessName: data.business_name || '',
  siret: data.siret || '',
  address: data.address || '',
  city: data.city || '',
  postalCode: data.postal_code || '',
  country: data.country || 'France',
  phone: data.phone || '',
  website: data.website || '',
  socialLinks: normalizeSocialLinks(data.social_links),
  description: data.description || '',
  bio: data.bio || '',
  story: data.story || '',
  knowHow: data.know_how || '',
  materials: formatStringList(data.materials),
  techniques: formatStringList(data.techniques),
  profileImage: data.profile_image ? normalizePhotoFromApi(data.profile_image) : null,
  bannerImage: data.banner_image ? normalizePhotoFromApi(data.banner_image) : null,
  shopSlug: data.shop_slug || null,
  directPurchase: Boolean(data.direct_purchase),
  activeProductsCount: data.active_products_count ?? 0,
  profileCompletion: data.profile_completion || null,
  isVerified: Boolean(data.is_verified),
  cguAccepted: Boolean(data.cgu_accepted),
  cguAcceptedAt: data.cgu_accepted_at || null,
});

const mapInfoToApi = (data = {}) => {
  const payload = {
    business_name: data.businessName,
    siret: data.siret,
    address: data.address,
    city: data.city,
    postal_code: data.postalCode,
    country: data.country,
    phone: data.phone,
    website: data.website,
    description: data.description,
    bio: data.bio,
    story: data.story,
    know_how: data.knowHow,
    materials: parseStringList(data.materials),
    techniques: parseStringList(data.techniques),
  };

  if (Object.prototype.hasOwnProperty.call(data, 'socialLinks')) {
    payload.social_links = socialLinksForApi(data.socialLinks);
  }

  if (Object.prototype.hasOwnProperty.call(data, 'profileImage')) {
    payload.profile_image = data.profileImage;
  }

  if (Object.prototype.hasOwnProperty.call(data, 'bannerImage')) {
    payload.banner_image = data.bannerImage;
  }

  return payload;
};

const formatStringList = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string' && item.trim() !== '').join(', ');
  }
  return typeof value === 'string' ? value : '';
};

const parseStringList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean);
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

class CreatorService {
  async createCreator(shopName, cguAccepted, shopSlug = null) {
    try {
      const body = {
        shop_name: shopName,
        cgu_accepted: cguAccepted,
      };

      if (shopSlug) {
        body.shop_slug = shopSlug;
      }

      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur lors de la création du créateur:', error);
      throw error;
    }
  }

  async getCreatorInfo() {
    try {
      const response = await axios.get(`${API_BASE_URL}/info`);
      return mapInfoFromApi(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des infos créateur:', error);
      throw error;
    }
  }

  async updateCreatorInfo(creatorData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/info`, mapInfoToApi(creatorData));
      NotificationService.success('Informations mises à jour avec succès');
      return mapInfoFromApi(response.data.data);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des infos créateur:', error);
      if (
        error.response?.status !== 401 &&
        error.response?.status !== 403
      ) {
        NotificationService.error('Erreur lors de la sauvegarde');
      }
      throw error;
    }
  }
}

export default new CreatorService();
